import { useParams, Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ArrowLeft, ArrowRight, Download, ImageOff, Clock, TrendingUp, Move, Maximize2, TrendingDown, Share2, Check, MessageCircle, Eye, EyeOff } from "lucide-react";
import { Reveal, Overline } from "@/components/landing/primitives";
import { CASES, CASES_BY_SLUG } from "@/content/cases";
import { BeforeAfterSlider } from "@/components/pages/BeforeAfterSlider";
import { Lightbox } from "@/components/pages/Lightbox";

function NotFound() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6 text-center" data-testid="case-not-found">
      <p className="font-serif text-5xl">Caso non trovato</p>
      <Link to="/risultati" className="link-underline text-sm">Torna a Percorsi e Risultati</Link>
    </div>
  );
}

function BeforeAfterSlot({ label, src, alt }) {
  return (
    <div className="relative bg-sand/50 border border-ink/10 aspect-[4/5] overflow-hidden" data-testid={`case-image-${label.toLowerCase()}`}>
      <span className="absolute top-4 left-4 z-10 bg-canvas/90 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-ink/70">{label}</span>
      {src ? (
        <img src={src} alt={alt} className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 text-ink/35">
          <ImageOff className="w-7 h-7 mb-3" strokeWidth={1.2} />
          <p className="text-xs uppercase tracking-[0.2em]">Documentazione clinica</p>
          <p className="mt-2 text-[11px] leading-relaxed max-w-[220px] text-ink/40">
            Le immagini reali del percorso verranno pubblicate con il consenso del paziente.
          </p>
        </div>
      )}
    </div>
  );
}

function ScoreBar({ before, after, max = 5 }) {
  const b = Math.min(1, parseFloat(String(before).replace(",", ".")) / max);
  const a = Math.min(1, parseFloat(String(after).replace(",", ".")) / max);
  return (
    <div className="relative h-1.5 w-full bg-ink/10 rounded-full overflow-hidden">
      <div className="absolute inset-y-0 left-0 bg-ink/25 rounded-full" style={{ width: `${b * 100}%` }} />
      <div className="absolute inset-y-0 left-0 bg-sage rounded-full transition-all" style={{ width: `${a * 100}%` }} />
    </div>
  );
}

function ScorePanel({ scores, testid }) {
  if (!scores) return null;
  const { overall, zones, title } = scores;
  return (
    <div className="mt-5 border border-sage/30 bg-sage/[0.07]" data-testid={testid}>
      <div className="flex flex-col gap-3 px-5 py-4 border-b border-sage/20">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-full bg-sage/15 flex items-center justify-center shrink-0">
              <TrendingDown className="w-5 h-5 text-sagedark" />
            </span>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-ink/45">{title} · complessivo</p>
              <p className="text-lg font-light">
                <span className="text-ink/45 line-through">{overall.before}</span>
                <span className="mx-2 text-ink/40">→</span>
                <span className="font-medium text-sagedark">{overall.after}</span>
                <span className="ml-2 text-xs text-sagedark">({overall.delta})</span>
              </p>
            </div>
          </div>
          <span className="sm:ml-auto text-[11px] text-ink/45 leading-snug">Valore più basso = pelle più uniforme</span>
        </div>
        <ScoreBar before={overall.before} after={overall.after} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-sage/20">
        {zones.map((z) => (
          <div key={z.name} className="px-5 py-3.5">
            <p className="text-[10px] uppercase tracking-[0.15em] text-ink/45">{z.name}</p>
            <p className="mt-1 mb-2 text-sm">
              <span className="text-ink/45 line-through">{z.before}</span>
              <span className="mx-1.5 text-ink/40">→</span>
              <span className="font-medium text-ink/85">{z.after}</span>
            </p>
            <ScoreBar before={z.before} after={z.after} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function CasePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [lightbox, setLightbox] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showPoints, setShowPoints] = useState(true);
  const c = CASES_BY_SLUG[slug];
  if (!c) return <NotFound />;

  const summaryEntries = Object.entries(c.summary || {});
  const others = CASES.filter((x) => x.slug !== slug).slice(0, 3);
  const gallery = c.gallery || [];
  const details = c.details || [];

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = `${c.title} — Percorso clinico · Dott. Alessandro Valle`;
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;
  const onShare = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: c.title, text: shareText, url: shareUrl });
        return;
      } catch (e) {
        /* utente ha annullato */
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      /* noop */
    }
  };

  return (
    <div className="bg-canvas text-ink" data-testid="case-page">
      <article>
        <section className="pt-36 md:pt-44 pb-10">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10">
            <Reveal>
              <div className="flex items-center gap-2 text-sm text-muted mb-10">
                <Link to="/" className="hover:text-ink link-underline">Home</Link>
                <span>/</span>
                <Link to="/risultati" className="hover:text-ink link-underline">Percorsi e Risultati</Link>
              </div>
            </Reveal>
            <Reveal><Overline>Percorso clinico</Overline></Reveal>
            <Reveal delay={0.1} as="h1" className="mt-6 font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl" >
              {c.title}
            </Reveal>
            <Reveal delay={0.18} as="p" className="mt-6 font-serif text-xl md:text-2xl font-light text-ink/65 max-w-2xl">
              {c.protocol}
            </Reveal>
            {c.tags && c.tags.length > 0 && (
              <Reveal delay={0.24} as="div" className="mt-7 flex flex-wrap gap-2.5">
                {c.tags.map((t) => (
                  <span key={t} className="border border-ink/15 px-3 py-1.5 text-[12px] text-ink/60">{t}</span>
                ))}
              </Reveal>
            )}
            <Reveal delay={0.3} as="div" className="mt-8 flex flex-wrap items-center gap-3">
              <button onClick={onShare} data-testid="case-share" className="group inline-flex items-center gap-2 border border-ink/20 px-5 py-2.5 text-xs uppercase tracking-[0.12em] hover:border-ink hover:bg-ink hover:text-canvas transition-colors duration-400">
                {copied ? <Check className="w-4 h-4 text-sagedark group-hover:text-canvas" /> : <Share2 className="w-4 h-4" />}
                {copied ? "Link copiato" : "Condividi"}
              </button>
              <a href={whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="case-share-whatsapp" aria-label="Condividi su WhatsApp" className="inline-flex items-center gap-2 border border-ink/20 px-5 py-2.5 text-xs uppercase tracking-[0.12em] hover:border-sagedark hover:text-sagedark transition-colors duration-400">
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
            </Reveal>
          </div>
        </section>

        {/* Prima / Dopo */}
        <section className="pb-16">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10">
            {Array.isArray(c.compares) && c.compares.length > 0 ? (
              <div className="mb-4" data-testid="case-compares">
                <p className="text-[11px] uppercase tracking-[0.35em] text-sagedark mb-3">Confronto interattivo</p>
                <p className="text-sm text-ink/55 max-w-2xl mb-8">Le foto sono accoppiate per lato del viso. Trascina il cursore su ogni immagine per confrontare prima e dopo.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-10 max-w-[720px] mx-auto">
                  {c.compares.map((cmp, i) => (
                    <figure key={i} data-testid={`case-compare-${i}`}>
                      <BeforeAfterSlider
                        before={cmp.before}
                        after={cmp.after}
                        beforeLabel={cmp.beforeLabel}
                        afterLabel={cmp.afterLabel}
                        beforeAlt={`${c.title} — ${cmp.label} prima`}
                        afterAlt={`${c.title} — ${cmp.label} dopo`}
                        ratio={cmp.ratio || "3 / 4"}
                      />
                      <figcaption className="mt-3">
                        <span className="text-[12px] font-medium text-ink/80">{cmp.label}</span>
                        {cmp.caption && <span className="block text-xs text-ink/50 leading-relaxed">{cmp.caption}</span>}
                      </figcaption>
                    </figure>
                  ))}
                </div>
                <p className="mt-6 flex items-center justify-center gap-2 text-xs text-ink/45">
                  <Move className="w-3.5 h-3.5" />
                  Trascina il cursore per confrontare prima e dopo
                </p>
              </div>
            ) : c.compare ? (
              <div className="mb-14">
                <p className="text-[11px] uppercase tracking-[0.35em] text-sagedark mb-6">Confronto interattivo</p>
                <div className="max-w-[560px] mx-auto">
                  <BeforeAfterSlider
                    before={c.compare.before}
                    after={c.compare.after}
                    beforeLabel={c.compare.beforeLabel}
                    afterLabel={c.compare.afterLabel}
                    beforeAlt={`${c.title} — prima`}
                    afterAlt={`${c.title} — dopo`}
                    ratio={c.compare.ratio || "1 / 1"}
                  />
                  <p className="mt-3 flex items-center justify-center gap-2 text-xs text-ink/45">
                    <Move className="w-3.5 h-3.5" />
                    Trascina il cursore per confrontare prima e dopo
                  </p>
                </div>
              </div>
            ) : null}

            {gallery.length > 0 && (
              <>
                <p className="text-[11px] uppercase tracking-[0.35em] text-sagedark mb-6">Prima e dopo</p>
                <div className="space-y-10">
                  {gallery.map((g, i) => (
                    <figure key={i} data-testid={`case-gallery-${i}`}>
                      <button
                        type="button"
                        onClick={() => setLightbox(i)}
                        data-testid={`case-gallery-open-${i}`}
                        className="group relative block w-full bg-black border border-ink/10 overflow-hidden cursor-zoom-in"
                      >
                        <img src={g.src} alt={`${c.title} — ${g.label}`} className="w-full h-auto block" loading={i === 0 ? "eager" : "lazy"} />
                        <span className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-white/85 opacity-80 group-hover:opacity-100 group-hover:bg-black/70 transition-all">
                          <Maximize2 className="w-4 h-4" />
                        </span>
                      </button>
                      <figcaption className="mt-3 flex flex-col sm:flex-row sm:items-baseline gap-x-3 gap-y-1">
                        <span className="text-[11px] uppercase tracking-[0.2em] text-sagedark shrink-0">{g.label}</span>
                        <span className="text-xs text-ink/50 leading-relaxed">{g.caption}</span>
                      </figcaption>
                      {g.scores && <ScorePanel scores={g.scores} testid={`case-scores-${i}`} />}
                    </figure>
                  ))}
                </div>
              </>
            )}
            {gallery.length === 0 && !c.compares && !c.compare && (
              <>
                <p className="text-[11px] uppercase tracking-[0.35em] text-sagedark mb-6">Prima e dopo</p>
                <div className="grid grid-cols-2 gap-px bg-ink/10 border border-ink/10">
                  <BeforeAfterSlot label="Prima" src={c.before} alt={`${c.title} — prima del trattamento`} />
                  <BeforeAfterSlot label="Dopo" src={c.after} alt={`${c.title} — dopo il trattamento`} />
                </div>
              </>
            )}
            <p className="mt-5 text-xs text-ink/45 leading-relaxed max-w-2xl">
              Le immagini prima/dopo sono sempre presentate nel loro contesto clinico. Indicazioni,
              tempi e risposta ai trattamenti sono individuali.
            </p>
            {c.observed && c.observed.length > 0 && (
              <div className="mt-7" data-testid="case-observed">
                <p className="text-[11px] uppercase tracking-[0.2em] text-muted mb-3">Risultati osservati</p>
                <div className="flex flex-wrap gap-2.5">
                  {c.observed.map((o, i) => (
                    <span key={i} className="inline-flex items-center gap-2 border border-sage/30 bg-sage/[0.07] px-4 py-2 text-[13px] text-ink/75">
                      <Check className="w-4 h-4 text-sagedark shrink-0" />
                      {o}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {c.measures && c.measures.length > 0 && (
              <div className="mt-7 border border-sage/30 bg-sage/[0.07] max-w-lg" data-testid="case-measures">
                <div className="px-5 py-3 border-b border-sage/20 flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-sagedark" />
                  <p className="text-[11px] uppercase tracking-[0.18em] text-ink/55">Misurazioni · circonferenze (cm)</p>
                </div>
                <div className="divide-y divide-sage/15">
                  {c.measures.map((m, i) => (
                    <div key={i} className="px-5 py-3.5">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-[13px] text-ink/70">{m.label}</span>
                        <span className="text-sm">
                          <span className="text-ink/45 line-through">{m.before}</span>
                          <span className="mx-1.5 text-ink/40">→</span>
                          <span className="font-medium text-sagedark">{m.after} cm</span>
                          <span className="ml-2 text-xs text-sagedark">(−{m.before - m.after} cm)</span>
                        </span>
                      </div>
                      <div className="mt-2">
                        <ScoreBar before={m.before} after={m.after} max={m.before} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {details.length > 0 && (
          <section className="pb-16" data-testid="case-details">
            <div className="max-w-[1100px] mx-auto px-6 md:px-10">
              <p className="text-[11px] uppercase tracking-[0.35em] text-sagedark mb-2">Zoom sul volto</p>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
                <p className="text-sm text-ink/55 max-w-2xl">Dettaglio ravvicinato di alcune zone: trascina il cursore per confrontare prima e dopo. I punti numerati indicano le rughe trattate; l'elenco sotto ogni immagine ne riporta la legenda.</p>
                <button
                  onClick={() => setShowPoints((v) => !v)}
                  data-testid="toggle-points"
                  aria-pressed={showPoints}
                  className="shrink-0 inline-flex items-center gap-2 border border-ink/20 px-4 py-2 text-[12px] uppercase tracking-[0.1em] text-ink/70 hover:border-ink hover:text-ink transition-colors duration-300"
                >
                  {showPoints ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {showPoints ? "Nascondi numeri" : "Mostra numeri"}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {details.map((d, i) => (
                  <div key={i} data-testid={`case-detail-${i}`}>
                    <BeforeAfterSlider
                      before={d.before}
                      after={d.after}
                      beforeLabel="Prima"
                      afterLabel="Dopo"
                      beforeAlt={`${c.title} — ${d.label} prima`}
                      afterAlt={`${c.title} — ${d.label} dopo`}
                      ratio={d.ratio || "2 / 1"}
                      annotations={showPoints ? d.annotations || [] : []}
                    />
                    <p className="mt-3 text-[13px] font-medium text-ink/80">{d.label}</p>
                    {d.note && <p className="text-xs text-ink/50 leading-relaxed">{d.note}</p>}
                    {d.annotations && d.annotations.length > 0 && (
                      <ol className="mt-3 space-y-1.5" data-testid={`case-legend-${i}`}>
                        {d.annotations.map((a, j) => (
                          <li key={j} className="flex items-start gap-2.5 text-[13px] text-ink/70">
                            <span className="mt-px w-5 h-5 rounded-full bg-sage/15 text-sagedark text-[11px] font-semibold flex items-center justify-center shrink-0">{j + 1}</span>
                            <span className="leading-snug">
                              {a.label}
                              {a.score && (
                                <span className="ml-2 inline-flex items-center gap-1 whitespace-nowrap text-[11px] font-medium text-sagedark align-middle">
                                  <TrendingDown className="w-3 h-3" />
                                  AURA {a.score.before} → {a.score.after}
                                </span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="pb-8">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10 grid lg:grid-cols-[1fr_360px] gap-12 lg:gap-16 items-start">
            {/* Descrizione clinica */}
            <div className="order-2 lg:order-1">
              <p className="text-[11px] uppercase tracking-[0.35em] text-sagedark mb-6">Descrizione del percorso</p>
              {(c.body || []).map((p, i) => (
                <Reveal key={i} as="p" className="mb-5 text-[17px] leading-[1.75] text-ink/85">{p}</Reveal>
              ))}

              {c.note && (
                <div className="mt-8 bg-sand border-l-2 border-sage p-6 md:p-7" data-testid="case-note">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-muted mb-3">Nota realistica sui risultati</p>
                  <p className="text-[15px] text-ink/75 leading-relaxed">{c.note}</p>
                </div>
              )}
            </div>

            {/* Box riepilogo */}
            <aside className="order-1 lg:order-2 lg:sticky lg:top-28" data-testid="case-summary">
              <div className="border border-ink/12 bg-canvas">
                <div className="bg-graphite text-canvas px-6 py-5">
                  <p className="text-[11px] uppercase tracking-[0.25em] text-canvas/60">In sintesi</p>
                </div>
                <dl className="divide-y divide-ink/10">
                  {summaryEntries.map(([k, v]) => (
                    <div key={k} className="px-6 py-4">
                      <dt className="text-[11px] uppercase tracking-[0.15em] text-ink/45">{k}</dt>
                      <dd className="mt-1.5 text-[15px] text-ink/85 leading-snug">{v}</dd>
                    </div>
                  ))}
                </dl>
                <div className="px-6 py-5 border-t border-ink/10 space-y-3 bg-sand/40">
                  {c.summary && c.summary["Rivalutazione fotografica"] && (
                    <p className="flex items-start gap-3 text-[13px] text-ink/65 leading-snug">
                      <Clock className="w-4 h-4 mt-0.5 text-sagedark shrink-0" />
                      Rivalutazione fotografica: {c.summary["Rivalutazione fotografica"]}
                    </p>
                  )}
                  {c.summary && c.summary["Massimo effetto atteso"] && (
                    <p className="flex items-start gap-3 text-[13px] text-ink/65 leading-snug">
                      <TrendingUp className="w-4 h-4 mt-0.5 text-sagedark shrink-0" />
                      Massimo effetto atteso: {c.summary["Massimo effetto atteso"]}
                    </p>
                  )}
                </div>

                {/* Download protocollo PDF */}
                <div className="px-6 py-5 border-t border-ink/10">
                  {c.pdf ? (
                    <a
                      href={c.pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="case-pdf-download"
                      className="group inline-flex w-full items-center justify-between gap-2 border border-ink px-5 py-3.5 text-xs uppercase tracking-[0.15em] hover:bg-ink hover:text-canvas transition-colors duration-500"
                    >
                      Scarica il protocollo
                      <Download className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                    </a>
                  ) : (
                    <div data-testid="case-pdf-pending" className="border border-dashed border-ink/20 px-5 py-3.5 text-[12px] text-ink/40 flex items-center gap-2">
                      <Download className="w-4 h-4 shrink-0" />
                      {c.pdfLabel ? `Protocollo "${c.pdfLabel}" in preparazione` : "Protocollo PDF in preparazione"}
                    </div>
                  )}
                </div>
              </div>
            </aside>
          </div>
        </section>

        {/* CTA */}
        <section className="pt-6 pb-20">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10">
            <div className="bg-graphite text-canvas p-7 md:p-10">
              <p className="font-serif text-2xl md:text-3xl leading-snug max-w-2xl">
                Vuoi capire se questo percorso è indicato nel tuo caso?
              </p>
              <p className="mt-3 text-sm text-canvas/60 leading-relaxed max-w-xl">
                Ogni protocollo viene definito dopo una valutazione medica individuale.
              </p>
              <div className="mt-7 flex flex-wrap gap-4">
                <button onClick={() => navigate("/contatti")} data-testid="case-cta" className="group relative inline-flex items-center overflow-hidden border border-canvas/40 px-8 py-3.5 text-xs uppercase tracking-[0.2em]">
                  <span className="absolute inset-0 bg-canvas translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
                  <span className="relative group-hover:text-graphite transition-colors duration-500">Prenota una valutazione</span>
                </button>
                <Link to="/risultati" data-testid="case-back" className="inline-flex items-center gap-2 text-sm text-canvas/75 hover:text-canvas link-underline">
                  <ArrowLeft className="w-4 h-4" /> Tutti i percorsi
                </Link>
              </div>
            </div>
          </div>
        </section>
      </article>

      {others.length > 0 && (
        <section className="py-16 md:py-24 bg-sand">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10">
            <Overline>Altri percorsi</Overline>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
              {others.map((o) => (
                <Link key={o.slug} to={`/risultati/${o.slug}`} className="group bg-canvas p-7 hover:bg-stone transition-colors duration-500">
                  <span className="text-[11px] uppercase tracking-[0.15em] text-sagedark">{o.category}</span>
                  <h3 className="mt-3 font-serif text-xl leading-tight">{o.title}</h3>
                  <span className="mt-4 inline-flex items-center gap-2 text-xs text-ink/50 group-hover:text-ink">
                    Vedi il percorso <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {lightbox !== null && (
        <Lightbox images={gallery} index={lightbox} onClose={() => setLightbox(null)} onIndex={setLightbox} />
      )}
    </div>
  );
}
